import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { BillingPlanId } from "@/services/api/billingService"
import type { StripeCheckoutElementsValue } from "@stripe/react-stripe-js/checkout"
import { CircleAlert, LockKeyhole, Mail, X } from "lucide-react"
import { useState } from "react"
import { PLAN_COPY } from "./planCopy"

/** A label on the left, an amount on the right, like the status row of the subscription card. */
const SummaryRow: React.FC<{
  label: React.ReactNode
  amount: string
  action?: React.ReactNode
}> = ({ label, amount, action }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex min-w-0 items-center gap-1">
      {label}
      {action}
    </div>
    <Typography as="small" className="shrink-0 tabular-nums">
      {amount}
    </Typography>
  </div>
)

/** "Add promotion code", opening into a field; Stripe checks the code. */
const PromotionCode: React.FC<{
  checkout: StripeCheckoutElementsValue
  /** A payment runs: the total must not change under it. */
  disabled: boolean
  /** The code is being checked: paying now would race the new total. */
  onPendingChange: (pending: boolean) => void
}> = ({ checkout, disabled, onPendingChange }) => {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 w-fit text-muted-foreground"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        {translate("UPGRADE/CHECKOUT_PROMO_ADD")}
      </Button>
    )
  }
  const apply = async () => {
    setPending(true)
    onPendingChange(true)
    setError(null)
    try {
      const result = await checkout.applyPromotionCode(code.trim())
      if (result.type === "error") {
        setError(
          result.error.message || translate("UPGRADE/CHECKOUT_PROMO_INVALID"),
        )
      }
    } catch {
      setError(translate("UPGRADE/CHECKOUT_PROMO_INVALID"))
    } finally {
      setPending(false)
      onPendingChange(false)
    }
  }
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (code.trim()) void apply()
      }}
    >
      <div className="flex gap-2">
        <Input
          autoFocus
          value={code}
          onChange={(event) => setCode(event.target.value)}
          aria-label={translate("UPGRADE/CHECKOUT_PROMO_LABEL")}
          aria-invalid={error ? true : undefined}
          placeholder={translate("UPGRADE/CHECKOUT_PROMO_LABEL")}
        />
        <PendingButton
          type="submit"
          variant="outline"
          pending={pending}
          disabled={disabled}
        >
          {translate("UPGRADE/CHECKOUT_PROMO_APPLY")}
        </PendingButton>
      </div>
      {error && (
        <Typography as="small" className="text-destructive" role="alert">
          {error}
        </Typography>
      )}
    </form>
  )
}

interface OrderSummaryProps {
  plan: BillingPlanId
  checkout: StripeCheckoutElementsValue
  pending: boolean
  /** A promotion code is being applied or removed: the total is about to change. */
  promoPending: boolean
  onPromoPendingChange: (pending: boolean) => void
  error: string | null
  onSubmit: () => void
}

/** The order as Stripe will charge it, with the one pay button of the page. */
export const OrderSummary: React.FC<OrderSummaryProps> = ({
  plan,
  checkout,
  pending,
  promoPending,
  onPromoPendingChange,
  error,
  onSubmit,
}) => {
  const { translate } = useTranslation()
  const [removeError, setRemoveError] = useState<string | null>(null)
  const { total, recurring, discountAmounts, email } = checkout
  const yearly = recurring?.interval === "year"
  const removePromotionCode = async () => {
    onPromoPendingChange(true)
    setRemoveError(null)
    try {
      const result = await checkout.removePromotionCode()
      if (result.type === "error") {
        setRemoveError(
          result.error.message ||
            translate("UPGRADE/CHECKOUT_PROMO_REMOVE_FAILED"),
        )
      }
    } catch {
      setRemoveError(translate("UPGRADE/CHECKOUT_PROMO_REMOVE_FAILED"))
    } finally {
      onPromoPendingChange(false)
    }
  }
  return (
    <Card data-testid="checkout-summary">
      <CardHeader>
        <CardTitle>{translate("UPGRADE/CHECKOUT_SUMMARY")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <SummaryRow
          label={
            <div className="flex flex-col">
              <Typography as="small">
                {translate(PLAN_COPY[plan].name)}
              </Typography>
              <Typography as="muted">
                {translate(
                  yearly
                    ? "UPGRADE/SUMMARY_BILLED_YEARLY"
                    : "UPGRADE/SUMMARY_BILLED_MONTHLY",
                )}
              </Typography>
            </div>
          }
          amount={total.subtotal.amount}
        />
        {discountAmounts?.map((discount) => (
          <SummaryRow
            key={discount.promotionCode ?? discount.displayName}
            label={
              <div className="flex flex-col">
                <Typography as="muted">
                  {translate("UPGRADE/CHECKOUT_DISCOUNT", {
                    code: discount.promotionCode ?? discount.displayName,
                  })}
                </Typography>
                {discount.recurring && (
                  <Typography as="muted" className="text-xs">
                    {discount.recurring.type === "repeating"
                      ? translate("UPGRADE/CHECKOUT_DISCOUNT_MONTHS", {
                          months: String(discount.recurring.durationInMonths),
                        })
                      : translate("UPGRADE/CHECKOUT_DISCOUNT_FOREVER")}
                  </Typography>
                )}
              </div>
            }
            action={
              <Button
                variant="ghost"
                size="icon"
                className="size-6 text-muted-foreground"
                aria-label={translate("UPGRADE/CHECKOUT_PROMO_REMOVE")}
                onClick={() => void removePromotionCode()}
                disabled={pending || promoPending}
              >
                <X className="size-3.5" />
              </Button>
            }
            amount={`−${discount.amount}`}
          />
        ))}
        {removeError && (
          <Typography as="small" className="text-destructive" role="alert">
            {removeError}
          </Typography>
        )}
        {!discountAmounts?.length && (
          <PromotionCode
            checkout={checkout}
            disabled={pending}
            onPendingChange={onPromoPendingChange}
          />
        )}
        <SummaryRow
          label={
            <Typography as="muted">
              {translate("UPGRADE/CHECKOUT_TAX")}
            </Typography>
          }
          // Net prices: until the billing address is in, VAT is still open
          amount={
            checkout.tax.status === "ready"
              ? total.taxExclusive.amount
              : translate("UPGRADE/CHECKOUT_TAX_PENDING")
          }
        />
        <Separator />
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-4">
            <Typography as="large">
              {translate("UPGRADE/CHECKOUT_DUE_TODAY")}
            </Typography>
            <Typography
              as="large"
              className="tabular-nums"
              data-testid="checkout-total"
            >
              {total.total.amount}
            </Typography>
          </div>
          {recurring && (
            <Typography as="muted">
              {translate(
                yearly
                  ? "UPGRADE/CHECKOUT_THEN_YEARLY"
                  : "UPGRADE/CHECKOUT_THEN_MONTHLY",
                { amount: recurring.dueNext.total.amount },
              )}
            </Typography>
          )}
        </div>
        {error && (
          <Alert variant="destructive">
            <CircleAlert className="size-4" />
            <Typography as="small">{error}</Typography>
          </Alert>
        )}
        <PendingButton
          size="lg"
          className="w-full"
          pending={pending}
          disabled={promoPending}
          onClick={onSubmit}
        >
          {translate("UPGRADE/CHECKOUT_SUBMIT")}
        </PendingButton>
        <ul className="flex flex-col gap-2 text-muted-foreground">
          {email && (
            <li className="flex items-start gap-2">
              <Mail className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <Typography as="muted" className="text-xs">
                {translate("UPGRADE/CHECKOUT_INVOICE_TO", { email })}
              </Typography>
            </li>
          )}
          <li className="flex items-start gap-2">
            <LockKeyhole
              className="mt-0.5 size-3.5 shrink-0"
              aria-hidden="true"
            />
            <Typography as="muted" className="text-xs">
              {translate("UPGRADE/TRUST_SECURE_PAYMENT")}
            </Typography>
          </li>
        </ul>
      </CardContent>
    </Card>
  )
}
